import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Award, Target, CheckCircle, Circle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Belt colors and styling
const BELT_COLORS = {
  white: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', glow: '' },
  blue: { bg: 'bg-blue-600', border: 'border-blue-400', text: 'text-white', glow: 'shadow-blue-500/30' },
  purple: { bg: 'bg-purple-600', border: 'border-purple-400', text: 'text-white', glow: 'shadow-purple-500/30' },
  brown: { bg: 'bg-amber-800', border: 'border-amber-600', text: 'text-white', glow: 'shadow-amber-500/30' },
  black: { bg: 'bg-zinc-900', border: 'border-red-600', text: 'text-white', glow: 'shadow-red-500/30' },
};

const BELT_ORDER = ['white', 'blue', 'purple', 'brown', 'black'];

// Generic belt progression criteria (customizable per coach)
const PROGRESSION_CRITERIA = {
  white: {
    to: 'blue',
    timeframe: '1-2 years',
    criteria: [
      { category: 'Technical Knowledge', items: ['Basic positions (mount, guard, side control, back)', 'Fundamental escapes from each position', 'Basic submissions (armbar, triangle, RNC, kimura)', 'Guard passing fundamentals'] },
      { category: 'Practical Application', items: ['Demonstrate techniques during live rolling', 'Ability to defend against common attacks', 'Shows understanding of leverage and base'] },
      { category: 'Training Consistency', items: ['Regular attendance (2-3x per week minimum)', 'Participates in drilling and sparring', 'Shows dedication to improvement'] },
      { category: 'Competition (Optional)', items: ['Competed in at least 1 tournament', 'Shows competitive mindset'] },
    ]
  },
  blue: {
    to: 'purple',
    timeframe: '2-3 years',
    criteria: [
      { category: 'Technical Development', items: ['Developed a guard game (closed, open, or half)', 'Consistent passing from multiple positions', 'Chain submissions together', 'Counter common escapes'] },
      { category: 'Strategic Understanding', items: ['Recognizes and exploits openings', 'Adapts game plan during rolls', 'Controls pace of matches', 'Understands weight distribution'] },
      { category: 'Teaching Ability', items: ['Can explain basic techniques to beginners', 'Helps newer students during class', 'Shows leadership qualities'] },
      { category: 'Competition', items: ['Competed at blue belt level', 'Shows composure under pressure'] },
    ]
  },
  purple: {
    to: 'brown',
    timeframe: '1.5-2 years',
    criteria: [
      { category: 'Advanced Technique', items: ['Has a defined "A-game"', 'Dangerous from multiple positions', 'Advanced submission setups', 'Leg lock proficiency'] },
      { category: 'Problem Solving', items: ['Solves problems in real-time', 'Creates own techniques/variations', 'Shuts down opponents\' games', 'High-percentage finishing'] },
      { category: 'Mentorship', items: ['Actively teaches lower belts', 'Leads drilling sessions', 'Contributes to gym culture'] },
      { category: 'Competition', items: ['Competes regularly', 'Success at purple belt level'] },
    ]
  },
  brown: {
    to: 'black',
    timeframe: '1-2 years',
    criteria: [
      { category: 'Mastery', items: ['Complete technical knowledge', 'No significant weaknesses', 'Innovates and refines techniques', 'Excellent body mechanics'] },
      { category: 'Competition & Results', items: ['High-level competition experience', 'Consistent performance under pressure', 'Victories against other brown/black belts'] },
      { category: 'Teaching & Leadership', items: ['Can run full classes independently', 'Develops training programs', 'Mentors multiple students to blue belt+'] },
      { category: 'Character', items: ['Embodies BJJ values', 'Positive representation of the gym', 'Contributes to the community'] },
    ]
  },
  black: {
    to: null,
    timeframe: 'Lifetime journey',
    criteria: [
      { category: 'Continued Growth', items: ['Never stop learning', 'Adapt to evolving meta', 'Stay active in training'] },
      { category: 'Legacy', items: ['Produce high-level students', 'Contribute to BJJ knowledge', 'Give back to the community'] },
    ]
  }
};

// Belt Display Component
export const BeltDisplay = ({ belt = 'white', stripes = 0, size = 'md', showLabel = true }) => {
  const colors = BELT_COLORS[belt] || BELT_COLORS.white;
  const sizeClasses = {
    sm: 'h-4 w-24',
    md: 'h-6 w-32',
    lg: 'h-8 w-40',
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${sizeClasses[size]} ${colors.bg} ${colors.border} border-2 rounded-sm relative shadow-lg ${colors.glow}`}>
        {/* Black bar for rank */}
        <div className="absolute right-2 top-0 bottom-0 w-4 bg-zinc-900 flex items-center justify-center gap-0.5">
          {[...Array(stripes)].map((_, i) => (
            <div key={i} className="w-0.5 h-3/4 bg-white" />
          ))}
        </div>
      </div>
      {showLabel && (
        <span className="text-xs text-zinc-400 capitalize">{belt} Belt{stripes > 0 ? ` (${stripes} stripe${stripes > 1 ? 's' : ''})` : ''}</span>
      )}
    </div>
  );
};

// Belt Progression Timeline
export const BeltProgressionTimeline = ({ currentBelt = 'white', currentStripes = 0 }) => {
  const currentIndex = BELT_ORDER.indexOf(currentBelt);
  
  return (
    <div className="flex items-center justify-between w-full max-w-xl mx-auto py-4">
      {BELT_ORDER.map((belt, index) => {
        const colors = BELT_COLORS[belt];
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        
        return (
          <React.Fragment key={belt}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full ${colors.bg} ${colors.border} border-2 flex items-center justify-center ${isCurrent ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-zinc-900' : ''} ${colors.glow} shadow-lg`}>
                {isComplete && <CheckCircle className="w-5 h-5 text-green-500" />}
                {isCurrent && <span className="text-xs font-bold">{currentStripes}</span>}
              </div>
              <span className={`text-xs mt-2 capitalize ${isCurrent ? 'text-white font-bold' : 'text-zinc-500'}`}>
                {belt}
              </span>
            </div>
            {index < BELT_ORDER.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${index < currentIndex ? 'bg-green-500' : 'bg-zinc-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Belt Progression Requirements Component (for Athletes)
export const BeltProgressionRequirements = ({ currentBelt = 'white', onClose }) => {
  const requirements = PROGRESSION_CRITERIA[currentBelt];
  const [checkedItems, setCheckedItems] = useState({});
  
  if (!requirements) return null;
  
  const toggleItem = (categoryIdx, itemIdx) => {
    const key = `${categoryIdx}-${itemIdx}`;
    setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const totalItems = requirements.criteria.reduce((sum, cat) => sum + cat.items.length, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progressPercent = Math.round((checkedCount / totalItems) * 100);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BeltDisplay belt={currentBelt} size="md" showLabel={false} />
          <ChevronRight className="w-5 h-5 text-zinc-500" />
          <BeltDisplay belt={requirements.to || currentBelt} size="md" showLabel={false} />
        </div>
        <div className="text-right">
          <p className="text-zinc-400 text-sm">Typical Timeframe</p>
          <p className="text-white font-medium">{requirements.timeframe}</p>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-zinc-400">Self-Assessment Progress</span>
          <span className="text-white font-medium">{progressPercent}%</span>
        </div>
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-red-600 to-red-500"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      
      {/* Criteria List */}
      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
        {requirements.criteria.map((category, catIdx) => (
          <div key={catIdx} className="space-y-3">
            <h4 className="font-heading text-white uppercase tracking-wide text-sm flex items-center gap-2">
              <Target className="w-4 h-4 text-red-500" />
              {category.category}
            </h4>
            <ul className="space-y-2 pl-6">
              {category.items.map((item, itemIdx) => {
                const key = `${catIdx}-${itemIdx}`;
                const isChecked = checkedItems[key];
                return (
                  <li 
                    key={itemIdx}
                    onClick={() => toggleItem(catIdx, itemIdx)}
                    className={`flex items-start gap-3 cursor-pointer group ${isChecked ? 'text-green-400' : 'text-zinc-400'}`}
                  >
                    {isChecked ? (
                      <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 mt-0.5 text-zinc-600 group-hover:text-zinc-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${isChecked ? 'line-through' : ''}`}>{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      
      {/* Disclaimer */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-4">
        <p className="text-zinc-400 text-xs">
          <strong className="text-zinc-300">Note:</strong> These are general guidelines. Your coach has the final say on promotion based on your individual progress, attitude, and other factors. Focus on consistent training and trust the process.
        </p>
      </div>
    </div>
  );
};

// Main Belt Tracking Card Component
export const BeltTrackingCard = ({ fighter, onUpdateBelt, isCoach = false }) => {
  const [showProgressionDialog, setShowProgressionDialog] = useState(false);
  const currentBelt = fighter?.bjj_belt || 'white';
  const currentStripes = fighter?.bjj_stripes || 0;
  
  return (
    <>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Belt Rank
          </h3>
          {!isCoach && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowProgressionDialog(true)}
              className="text-zinc-400 hover:text-white text-xs"
            >
              View Requirements
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <BeltDisplay belt={currentBelt} stripes={currentStripes} size="lg" />
          
          <div className="text-right">
            <p className="text-zinc-500 text-sm">Current Rank</p>
            <p className="text-white font-heading text-xl uppercase">
              {currentBelt} Belt
            </p>
            {currentStripes > 0 && (
              <p className="text-zinc-400 text-sm">{currentStripes} Stripe{currentStripes > 1 ? 's' : ''}</p>
            )}
          </div>
        </div>
        
        <div className="mt-6">
          <BeltProgressionTimeline currentBelt={currentBelt} currentStripes={currentStripes} />
        </div>
      </div>
      
      {/* Progression Requirements Dialog */}
      <Dialog open={showProgressionDialog} onOpenChange={setShowProgressionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase tracking-tight flex items-center gap-2">
              <Target className="w-5 h-5 text-red-500" />
              Belt Progression Guide
            </DialogTitle>
          </DialogHeader>
          <BeltProgressionRequirements 
            currentBelt={currentBelt} 
            onClose={() => setShowProgressionDialog(false)} 
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BeltTrackingCard;
